import React, { useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API, getAuthHeader } from './ccmiConstants';

export function useCcmiCompliance(deps) {
  const { assigningJuriste, autosaveAt, autosaveBaselineRef, autosaveTimerRef, complianceDraft, complianceLoading, complianceSaving, compliancingDoc, _draft, juristeCommentInput, juristeEmailInput, selected, setAssigningJuriste, setAutosaveAt, setAutosaveError, setAutosaveStatus, setAutosaveTick, setComplianceDraft, setComplianceLoading, setComplianceSaving, setCompliancingDoc, setJuristeAssignLoading, setJuristeCommentInput, setJuristeEmailInput, setSelected } = deps;

  const openComplianceModal = (project, doc) => {
    setCompliancingDoc({ project, doc });
    // V38.17 — On rehydrate aussi les champs annexes depuis `compliance_data.*`
    // pour ne pas perdre les données saisies lors d'une session précédente.
    const cd = project.compliance_data || {};
    const draft = {
      maitre_ouvrage: { ...project.maitre_ouvrage },
      constructeur: { ...project.constructeur },
      terrain: { ...project.terrain },
      prix_ttc: project.prix_ttc ?? '',
      type_contrat: project.type_contrat || 'avec_plan',
      delai_execution: cd.delai_execution || '',
      penalites_retard: cd.penalites_retard || '',
      financement: cd.financement || '',
      permis: cd.permis || '',
      assurance_do: cd.assurance_do || '',
      garantie_livraison: cd.garantie_livraison || (project.constructeur && project.constructeur.garant) || '',
      notice_plans_annexes: cd.notice_plans_annexes || '',
      clauses_particulieres: cd.clauses_particulieres || '',
    };
    setComplianceDraft(draft);
    // V38.18 — Reset autosave state
    autosaveBaselineRef.current = JSON.stringify(draft);
    setAutosaveStatus(cd.last_updated_at ? 'saved' : 'idle');
    setAutosaveAt(cd.last_updated_at || null);
    setAutosaveError(null);
  };

  // V37.55 — Soumettre la mise en conformite (genere et telecharge le PDF)
  const submitComplianceDraft = async () => {
    if (!compliancingDoc || !complianceDraft) return;
    const { project, doc } = compliancingDoc;
    // Validation client (defense en profondeur, le backend revalide)
    const errs = [];
    if (!complianceDraft.maitre_ouvrage?.nom && !complianceDraft.maitre_ouvrage?.email)
      errs.push('Maitre d\'ouvrage : nom ou email requis');
    if (!complianceDraft.constructeur?.raison_sociale)
      errs.push('Constructeur : raison sociale requise');
    if (!complianceDraft.terrain?.adresse)
      errs.push('Terrain : adresse requise');
    if (!complianceDraft.prix_ttc || Number(complianceDraft.prix_ttc) <= 0)
      errs.push('Prix TTC : valeur positive requise');
    if (!['avec_plan', 'sans_plan'].includes(complianceDraft.type_contrat))
      errs.push('Type de contrat : avec_plan ou sans_plan');
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    setComplianceLoading(true);
    try {
      const payload = { ...complianceDraft, prix_ttc: Number(complianceDraft.prix_ttc) };
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/documents/${doc.document_id}/compliance-draft-pdf`,
        payload,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `projet_mise_en_conformite_ccmi_${project.reference || 'sans-ref'}_${doc.document_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const gedStored = res.headers?.['x-ged-stored'] === '1';
      const newDocId = res.headers?.['x-document-id'];
      toast.success(
        `Projet de mise en conformité généré${gedStored ? ' et archivé GED' : ''}${newDocId ? ` (${newDocId})` : ''}`,
      );
      setCompliancingDoc(null);
      setComplianceDraft(null);
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${project.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || project);
      } catch { /* non bloquant */ }
    } catch (err) {
      let detail = err?.response?.data?.detail;
      if (!detail && err?.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          detail = JSON.parse(txt)?.detail;
        } catch { /* ignore */ }
      }
      if (detail && typeof detail === 'object' && detail.validation_errors) {
        toast.error(detail.validation_errors[0] || 'Erreur validation');
      } else {
        toast.error(detail || 'Erreur génération mise en conformité');
      }
    } finally {
      setComplianceLoading(false);
    }
  };

  // V38.17 — Sauvegarder les données saisies (SANS générer de PDF)
  const saveComplianceDraft = async () => {
    if (!compliancingDoc || !complianceDraft) return;
    const { project, doc } = compliancingDoc;
    setComplianceSaving(true);
    try {
      const payload = {
        ...complianceDraft,
        prix_ttc: complianceDraft.prix_ttc === '' ? null : Number(complianceDraft.prix_ttc),
      };
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/documents/${doc.document_id}/compliance-draft-save`,
        payload,
        { headers: getAuthHeader() },
      );
      const fields = res.data?.fields_saved || 0;
      toast.success(`Données enregistrées (${fields} champ${fields > 1 ? 's' : ''})`);
      // Refresh project state so re-opening reflects the saved values
      const updatedProject = res.data?.project;
      if (updatedProject) {
        setSelected(updatedProject);
        // Refresh modal context (project ref keeps the same id)
        setCompliancingDoc((prev) => prev ? { project: updatedProject, doc: prev.doc } : prev);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur enregistrement';
      toast.error(`Échec enregistrement : ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
    } finally {
      setComplianceSaving(false);
    }
  };

  // V38.18 — Autosave silencieux (debounce 2s)
  //
  // Quand l'utilisateur modifie le `complianceDraft`, on attend 2 secondes
  // d'inactivité puis on persiste via /compliance-draft-save. Le statut est
  // affiché en haut du modal ("Enregistrement...", "Enregistré il y a Xs",
  // ou "Échec d'enregistrement").
  //
  // Garanties :
  //  - Ne déclenche aucun appel si le draft = baseline (pas de modification).
  //  - Ne déclenche pas si une sauvegarde manuelle ou la génération PDF est
  //    en cours (évite les conflits).
  //  - Nettoyage propre du timer à la fermeture du modal.
  const triggerAutosave = useCallback(async (project, doc, draft) => {
    if (!project || !doc || !draft) return;
    setAutosaveStatus('saving');
    try {
      const payload = {
        ...draft,
        prix_ttc: draft.prix_ttc === '' ? null : Number(draft.prix_ttc),
      };
      const res = await axios.post(
        `${API}/ccmi/projects/${project.id}/documents/${doc.document_id}/compliance-draft-save`,
        payload,
        { headers: getAuthHeader() },
      );
      const updatedProject = res.data?.project;
      const savedAt = res.data?.saved_at || new Date().toISOString();
      setAutosaveAt(savedAt);
      setAutosaveStatus('saved');
      setAutosaveError(null);
      autosaveBaselineRef.current = JSON.stringify(draft);
      if (updatedProject) {
        setSelected(updatedProject);
        setCompliancingDoc((prev) => prev ? { project: updatedProject, doc: prev.doc } : prev);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur autosave';
      setAutosaveStatus('error');
      setAutosaveError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  }, []);

  useEffect(() => {
    // Cleanup au démontage
    if (!compliancingDoc || !complianceDraft) {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      return undefined;
    }
    // Skip si baseline non posée (sécurité)
    if (autosaveBaselineRef.current === null) return undefined;
    // Skip si aucune modification
    if (JSON.stringify(complianceDraft) === autosaveBaselineRef.current) return undefined;
    // Skip si actions concurrentes en cours
    if (complianceLoading || complianceSaving) return undefined;

    setAutosaveStatus('pending');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      const { project, doc } = compliancingDoc;
      triggerAutosave(project, doc, complianceDraft);
    }, 2000); // debounce 2s
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [complianceDraft, compliancingDoc, complianceLoading, complianceSaving, triggerAutosave]);

  // Re-render périodique pour rafraîchir le libellé "il y a Xs"
  useEffect(() => {
    if (!compliancingDoc || !autosaveAt) return undefined;
    const id = setInterval(() => setAutosaveTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [compliancingDoc, autosaveAt]);

  // V37.56 — Assigner un juriste a un document compliance_draft_pdf
  const openJuristeAssign = (doc) => {
    setAssigningJuriste(doc);
    setJuristeEmailInput(doc?.juridique_review?.assigned_to || '');
    setJuristeCommentInput('');
  };

  const submitJuristeAssign = async () => {
    if (!assigningJuriste || !selected) return;
    const email = (juristeEmailInput || '').trim().toLowerCase();
    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Email juriste invalide');
      return;
    }
    setJuristeAssignLoading(true);
    try {
      await axios.post(
        `${API}/ccmi/projects/${selected.id}/documents/${assigningJuriste.document_id}/juriste/assign`,
        { assigned_to: email, comment: juristeCommentInput || null },
        { headers: getAuthHeader() },
      );
      toast.success(`Juriste assigné : ${email}`);
      setAssigningJuriste(null);
      setJuristeEmailInput('');
      setJuristeCommentInput('');
      try {
        const detail = await axios.get(`${API}/ccmi/projects/${selected.id}`, { headers: getAuthHeader() });
        setSelected(detail.data?.project || selected);
      } catch { /* non bloquant */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur assignation juriste');
    } finally {
      setJuristeAssignLoading(false);
    }
  };

  return { openComplianceModal, submitComplianceDraft, saveComplianceDraft, triggerAutosave, openJuristeAssign, submitJuristeAssign };
}
